import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { API } from './adminConstants';

const fmt = (v) => `R$ ${(v ?? 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
const ROLE_LABEL = { partner: 'Parceiro', affiliate: 'Afiliado', master: 'Você' };

const AdminDiscountAudit = () => {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    axios.get(`${API}/admin/discount-audit`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [getToken]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="discount-audit-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="w-5 h-5 text-[#C5A059]" /> Auditoria de Descontos</CardTitle>
        <p className="text-sm text-slate-500">Todos os descontos recorrentes concedidos por você, parceiros e afiliados — e quanto custam por mês.</p>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="w-6 h-6 animate-spin text-[#C5A059] mx-auto my-6" /> : !data || data.items.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6" data-testid="audit-empty">Nenhum desconto concedido até agora.</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="audit-summary">
              <span className="bg-[#0A2647] text-white rounded-lg px-3 py-2 text-xs font-semibold">
                Custo total: {fmt(data.total_monthly_cost)}/mês · {data.items.length} desconto{data.items.length > 1 ? 's' : ''}
              </span>
              {data.summary.map((s, i) => (
                <span key={i} className="bg-slate-100 rounded-lg px-3 py-2 text-xs" data-testid={`audit-seller-${i}`}>
                  <b className="text-[#0A2647]">{ROLE_LABEL[s.role] || s.role} {s.by}</b>: {s.count}x · média {s.avg_pct}% · {fmt(s.monthly_cost)}/mês
                </span>
              ))}
            </div>
            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs" data-testid="audit-table">
                <thead className="bg-slate-50 sticky top-0"><tr>
                  {['Cliente', 'Plano', 'Desc.', 'De → Por', 'Custo/mês', 'Quem deu', 'Motivo', 'Quando'].map(h => (
                    <th key={h} className="text-left px-2 py-1.5 font-semibold text-slate-500">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {data.items.map((it, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 font-medium">{it.company_name}</td>
                      <td className="px-2 py-1.5">{it.plan}</td>
                      <td className="px-2 py-1.5 font-bold text-[#C5A059]">{it.pct}%</td>
                      <td className="px-2 py-1.5 text-slate-500">{fmt(it.full_price)} → <b className="text-[#0A2647]">{fmt(it.final_price)}</b></td>
                      <td className="px-2 py-1.5 text-red-500">−{fmt(it.monthly_cost)}</td>
                      <td className="px-2 py-1.5">{ROLE_LABEL[it.role] || it.role} {it.by}</td>
                      <td className="px-2 py-1.5 text-slate-400">{it.reason || '—'}</td>
                      <td className="px-2 py-1.5 text-slate-400">{fmtDate(it.at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminDiscountAudit;
